/**
 * Accelerated checkout button, requested after the product image.
 *
 * docs/09 wants the button on every product page; docs/08 wants nothing
 * ahead of the LCP image. Rendering `payment_button` in the product document
 * makes Shopify inject its 77 kB portable-wallets module in the head at High
 * priority, where it shares the connection with the gallery image and sits
 * in Lighthouse's LCP graph (docs/lighthouse-baseline.md, section 10). So
 * the document carries a placeholder of the button's size (the skeleton
 * Shopify itself shows before its script runs) and this element fetches the
 * real markup from sections/product-payment-button.liquid through the
 * Section Rendering API once the shopper has touched, scrolled or keyed the
 * page or the buy box has entered the viewport, after the first slide has
 * loaded. It then calls window.Shopify.PaymentButton.init(), Shopify's own
 * bootstrap, present on every storefront page and the same call Dawn's
 * quick-add.js makes, which appends the module script. The wrapper keeps its
 * height throughout; a shopper who taps the placeholder before the button
 * exists is told through the status line when it arrives. If the fetch or
 * the script fails, the placeholder is removed and the add button's checkout
 * offers the same wallets.
 */
if (!customElements.get('deferred-payment-button')) {
  customElements.define(
    'deferred-payment-button',
    class DeferredPaymentButton extends HTMLElement {
      static TRIGGERS = ['pointerdown', 'keydown', 'touchstart', 'wheel'];

      connectedCallback() {
        this.status = this.querySelector('[role="status"]');
        this.onTrigger = () => this.queue();
        this.onClick = () => {
          this.tapped = true;
          this.queue();
          this.announce(this.dataset.loadingText);
        };
        DeferredPaymentButton.TRIGGERS.forEach((type) =>
          document.addEventListener(type, this.onTrigger, { once: true, passive: true }),
        );
        this.addEventListener('click', this.onClick);
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.queue();
          });
          this.observer.observe(this);
        } else {
          this.queue();
        }
      }

      disconnectedCallback() {
        this.stopListening();
      }

      stopListening() {
        DeferredPaymentButton.TRIGGERS.forEach((type) => document.removeEventListener(type, this.onTrigger));
        this.observer?.disconnect();
      }

      // Runs the load once, after the LCP image is in (docs/08: nothing
      // ahead of it), whichever trigger came first.
      queue() {
        if (this.queued) return;
        this.queued = true;
        this.stopListening();
        const image = document.querySelector('img[fetchpriority="high"]');
        if (image && !image.complete) {
          const start = () => this.load();
          image.addEventListener('load', start, { once: true });
          image.addEventListener('error', start, { once: true });
        } else {
          this.load();
        }
      }

      async load() {
        const form = this.closest('form');
        const variantId = form?.querySelector('input[name="id"]')?.value;
        const url = new URL(this.dataset.url, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.dataset.section);

        let markup = null;
        try {
          const response = await fetch(url);
          if (response.ok) {
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            markup = html.querySelector('.shopify-payment-button');
          }
        } catch (error) {
          markup = null;
        }
        if (!markup || typeof window.Shopify?.PaymentButton?.init !== 'function') {
          this.remove();
          return;
        }

        this.replaceChildren(markup);
        if (this.status) this.append(this.status);
        window.Shopify.PaymentButton.init();

        const defined = customElements.whenDefined('shopify-accelerated-checkout').then(() => true);
        const timeout = new Promise((resolve) => setTimeout(() => resolve(false), 10000));
        if (await Promise.race([defined, timeout])) {
          this.ready();
        } else {
          this.querySelectorAll('.shopify-payment-button__skeleton').forEach((element) => element.remove());
        }
      }

      // After a tap on the placeholder, say the button is there. Focus is
      // not moved: the wallet button keeps its control in a closed shadow
      // root, so it can be tabbed to but not focused from outside, and a
      // keyboard user's first key press loads the button long before they
      // reach it.
      ready() {
        if (this.tapped) this.announce(this.dataset.readyText);
      }

      announce(text) {
        if (this.status && text) this.status.textContent = text;
      }
    },
  );
}
